// Custom (Text-to-Course) course store with the same two-layer model
// the puzzle runner uses: localStorage as an offline cache, the
// backend DB as the durable home. On sign-in we pull the user's saved
// courses and merge them; anything created locally while signed out
// gets pushed up so the two stay aligned. Each `addCourse` writes
// localStorage immediately and fires a DB save when authenticated.

import { useCallback, useEffect, useState } from "react";

import { api } from "../auth/client";
import { useAuth } from "../auth/AuthProvider";
import { parseCourse } from "./parseCourse";
import type { Course } from "./types-and-data";

const STORAGE_KEY = "coscience.courses.custom.v1";

function readLocal(): Course[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    if (!Array.isArray(parsed)) return [];
    return parsed
      // Stored courses keep their id (preserveId) so they dedup cleanly.
      .map((c) => parseCourse(c, undefined, true))
      .filter((c): c is Course => c !== null);
  } catch {
    return [];
  }
}

function writeLocal(courses: Course[]): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(courses));
  } catch {
    // storage full / disabled — keep the in-memory copy for the session.
  }
}

/** Merge two course lists, de-duping by id (first wins). */
function mergeById(a: Course[], b: Course[]): Course[] {
  const seen = new Set(a.map((c) => c.id));
  return [...a, ...b.filter((c) => !seen.has(c.id))];
}

export interface CustomCoursesState {
  courses: Course[];
  addCourse: (course: Course) => void;
}

export function useCustomCourses(): CustomCoursesState {
  const { user } = useAuth();
  const [courses, setCourses] = useState<Course[]>(() => readLocal());

  // Sign-in sync: pull the user's DB courses, merge with whatever's
  // local, and push any local-only ones up so the server catches up.
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await api.listCourses();
        if (cancelled) return;
        const fromDb = res.courses
          .map((row) => parseCourse(row.payload, undefined, true))
          .filter((c): c is Course => c !== null);
        setCourses((prev) => {
          const merged = mergeById(prev, fromDb);
          writeLocal(merged);
          // Push local-only courses (not yet on the server) up.
          const dbIds = new Set(fromDb.map((c) => c.id));
          for (const c of prev) {
            if (!dbIds.has(c.id)) {
              void api.saveCourse(c.mode, c.title, c).catch(() => undefined);
            }
          }
          return merged;
        });
      } catch {
        // Network / auth failure — keep the local set as-is.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user]);

  const addCourse = useCallback(
    (course: Course) => {
      setCourses((prev) => {
        if (prev.some((c) => c.id === course.id)) return prev;
        const next = [...prev, course];
        writeLocal(next);
        return next;
      });
      // Persist to the DB when signed in (fire-and-forget; the local
      // copy already holds it, and the next sign-in sync reconciles).
      if (user) {
        void api.saveCourse(course.mode, course.title, course).catch(() => undefined);
      }
    },
    [user],
  );

  return { courses, addCourse };
}
